import React, { useEffect } from "react";
import { BettingEventStatus, FreeplayEvent, FreeplayGame, GameLeaderboardDataReturn } from "@freeplay/shared-code";
import { LeaderboardView } from "../views/LeaderboardView";
import { useReducer } from "react";
import { Option } from "react-dropdown";
import { useServices } from "../contexts/ServiceContext";
import { useScrollToTop } from "../components/hooks/scrollToTopHook";
import { useApplication } from "../contexts/ApplicationContext";

type LeaderboardControllerProps = {
    testID?: string;
};

export const LeaderboardController = ({ testID = "" }: LeaderboardControllerProps): JSX.Element => {
    const { eventsApi, gamesApi, fpUser } = useServices();
    const { appDispatch, appState } = useApplication();
    const { lbSelectId } = appState;
    const [state, dispatch] = useReducer(leaderReducer, {
        leaderboardData: [],
        currentSelection: { label: "Game Leaderboard", value: "-1" },
        dropdownOptions: [],
        games: [],
        events: [],
        bettingEndsEventId: -1,
        bettingStatus: BettingEventStatus.DRAFT,
    });

    useScrollToTop();
    useEffect(() => {
        if (!fpUser) {
            appDispatch({ type: "setLoginButtonVisible", buttonVisible: true, buttonString: "Login To Compete" });
        } else {
            appDispatch({ type: "setLoginButtonVisible", buttonVisible: false, buttonString: "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fpUser]);

    useEffect(() => {
        (async () => {
            const gameData = await gamesApi.getAllGames();
            let dropdownOptions: Option[] = [];
            let events: FreeplayEvent[] = [];

            for (const game of gameData) {
                dropdownOptions.push({ label: `Game: ${game.name}`, value: `g:${game.id}` });
                dropdownOptions = dropdownOptions.concat(
                    game.events
                        .filter((val) => val.eventStatus !== BettingEventStatus.DRAFT)
                        .map((val) => ({ label: `Event: ${val.name}`, value: `e:${val.id}` })),
                );
                events = events.concat(game.events);
            }
            dispatch({ type: "setEvents", events });
            dispatch({ type: "setDropdownOptions", dropdownOptions });
            if (lbSelectId) {
                const currentSelection = dropdownOptions.find((val) => val.value === `e:${lbSelectId}`);
                if (currentSelection) {
                    dispatch({ type: "setCurrentSelection", currentSelection });
                    const eventRes = await eventsApi.getLeaderboardForEvent(lbSelectId);
                    dispatch({ type: "setLeaderboardData", leaderboardData: eventRes });
                    dispatch({ type: "setBettingEndsEventId", bettingEndsEventId: +lbSelectId });
                    return;
                }
            }
            dispatch({ type: "setCurrentSelection", currentSelection: dropdownOptions[0] });
            const gameLB = await gamesApi.getGameLeaderboard(gameData[0].id);
            dispatch({ type: "setLeaderboardData", leaderboardData: gameLB });
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEventSelected = async (arg: Option) => {
        const pickInfo = arg.value.split(":");
        if (pickInfo[0] === "g") {
            const gameRes = await gamesApi.getGameLeaderboard(+pickInfo[1]);
            dispatch({ type: "setLeaderboardData", leaderboardData: gameRes });
            dispatch({ type: "setBettingEndsEventId", bettingEndsEventId: -1 });
        } else if (pickInfo[0] === "e") {
            const eventRes = await eventsApi.getLeaderboardForEvent(+pickInfo[1]);
            const event = state.events.filter((event) => event.id === parseInt(pickInfo[1]));
            dispatch({ type: "setBettingStatus", bettingStatus: event[0].eventStatus });
            dispatch({ type: "setLeaderboardData", leaderboardData: eventRes });
            dispatch({ type: "setBettingEndsEventId", bettingEndsEventId: +pickInfo[1] });
        }
    };

    return <LeaderboardView state={state} dispatch={dispatch} handleEventSelected={handleEventSelected} />;
};

export type LeaderboardState = {
    games: FreeplayGame[];
    events: FreeplayEvent[];
    leaderboardData: GameLeaderboardDataReturn[];
    dropdownOptions: Option[];
    currentSelection: Option;
    bettingEndsEventId: number;
    bettingStatus: BettingEventStatus;
};

export type LeaderboardActions =
    | { type: "setLeaderboardData"; leaderboardData: GameLeaderboardDataReturn[] }
    | { type: "setCurrentSelection"; currentSelection: Option }
    | { type: "setBettingEndsEventId"; bettingEndsEventId: number }
    | { type: "setDropdownOptions"; dropdownOptions: Option[] }
    | { type: "setGames"; games: FreeplayGame[] }
    | { type: "setEvents"; events: FreeplayEvent[] }
    | { type: "setBettingStatus"; bettingStatus: BettingEventStatus };

const leaderReducer = (state: LeaderboardState, action: LeaderboardActions): LeaderboardState => {
    switch (action.type) {
        case "setLeaderboardData":
            return { ...state, leaderboardData: action.leaderboardData };
        case "setCurrentSelection":
            return { ...state, currentSelection: action.currentSelection };
        case "setBettingEndsEventId":
            return { ...state, bettingEndsEventId: action.bettingEndsEventId };
        case "setDropdownOptions":
            return { ...state, dropdownOptions: action.dropdownOptions };
        case "setGames":
            return { ...state, games: action.games };
        case "setEvents":
            return { ...state, events: action.events };
        case "setBettingStatus":
            return { ...state, bettingStatus: action.bettingStatus };
    }
};
